import { Component, ElementRef, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { first, Subscription, tap } from 'rxjs';
import { SelectedCustomerService } from '../../../../services/selected-customer/selected-customer.service';
import { AiChatService } from '../../services/ai-chat.service';
import { ChatRefusedReason, ChatStreamEvent } from '../../services/chat-stream-event.model';
import { ConversationSummary } from '../../services/conversation-summary.model';
import { foldSegments, Segment } from './chat-segment';
import { renderMarkdown } from './markdown-lite';

interface Message {
  role: 'user' | 'assistant';
  segments: Segment[];
  errored: boolean;
  refusedReason?: ChatRefusedReason;
}

@Component({
  selector: 'gung-glippy-ai-chat',
  standalone: false,
  templateUrl: './ai-chat.component.html',
  styleUrl: './ai-chat.component.scss'
})
export class AiChatComponent implements OnInit, OnDestroy {

  // Embedded hosts (e.g. a customer details tab) hide the conversation sidebar.
  @Input() showConversationHistory = true;

  protected selectedCustomerService = inject(SelectedCustomerService);

  private aiChatService = inject(AiChatService);

  conversationId: string = crypto.randomUUID();
  conversations: ConversationSummary[] = [];
  messages: Message[] = [];
  inputValue = '';
  streaming = false;
  @ViewChild('messageList') private messageList?: ElementRef<HTMLElement>;

  private streamSub?: Subscription;
  private selectedCustomerId?: string;

  ngOnInit(): void {
    this.selectedCustomerService.getSelectedCustomer().pipe(
      tap(customer => {
        this.selectedCustomerId = customer?.id;
        this.aiChatService.getConversations(this.selectedCustomerId).pipe(first()).subscribe(conversations => {
          this.conversations = conversations;
        });
      }),
      first()
    ).subscribe();
  }

  selectConversation(conversation: ConversationSummary): void {
    if (conversation.id === this.conversationId) return;

    this.stop();
    this.conversationId = conversation.id;
    // TODO: load the conversation's messages once a history endpoint exists.
    this.messages = [];
  }

  newConversation(): void {
    this.stop();
    this.conversationId = crypto.randomUUID();
    this.messages = [];
  }

  send(): void {
    const text = this.inputValue.trim();
    if (!text || this.streaming) return;

    // The backend will eventually own conversation summaries; until then keep the
    // sidebar in sync client-side so a fresh conversation shows up immediately.
    if (!this.conversations.some(c => c.id === this.conversationId)) {
      this.conversations.unshift({ id: this.conversationId, title: text, updatedAt: new Date().toISOString() });
    }

    this.messages.push({ role: 'user', segments: [{ type: 'text', text }], errored: false });
    const assistantMsg: Message = { role: 'assistant', segments: [], errored: false };
    this.messages.push(assistantMsg);
    this.inputValue = '';
    this.streaming = true;

    this.streamSub = this.aiChatService.stream(this.conversationId, text, this.selectedCustomerId).subscribe({
      next: (events: ChatStreamEvent[]) => {
        // stream() re-parses from position 0 on every DownloadProgress tick, so
        // `events` always contains ALL events received so far - not just new ones.
        // Fold them into ordered segments; the fold is deterministic, so redoing it
        // every tick is idempotent.
        assistantMsg.segments = foldSegments(events);

        setTimeout(() => {
          if (this.messageList) {
            this.messageList.nativeElement.scrollTop = this.messageList.nativeElement.scrollHeight;
          }
        });

        const last = events.at(-1);
        if (last?.type === 'done') {
          this.streaming = false;
        } else if (last?.type === 'error') {
          assistantMsg.errored = true;
          this.streaming = false;
        } else if (last?.type === 'refused') {
          assistantMsg.refusedReason = last.reason;
          this.streaming = false;
        }
      },
      error: () => {
        assistantMsg.errored = true;
        this.streaming = false;
        this.streamSub = undefined;
      },
      complete: () => {
        this.streaming = false;
        this.streamSub = undefined;
      }
    });
  }

  hasContent(msg: Message): boolean {
    return msg.segments.length > 0 || msg.errored || !!msg.refusedReason;
  }

  refusedKey(msg: Message): string {
    return msg.refusedReason === 'budget' ? 'GLIPPY_REFUSED_BUDGET' : 'GLIPPY_REFUSED_DISABLED';
  }

  get waiting(): boolean {
    if (!this.streaming) return false;
    const last = this.messages.at(-1);
    return last?.role === 'assistant' && last.segments.length === 0;
  }

  stop(): void {
    this.streamSub?.unsubscribe();
    this.streamSub = undefined;
    this.streaming = false;
  }

  toHtml(text: string): string {
    return renderMarkdown(text);
  }

  ngOnDestroy(): void {
    this.streamSub?.unsubscribe();
    this.streamSub = undefined;
  }
}
